import React, { useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { formatChartTitle } from "../../utils";

interface DonutChartData {
  label: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  title?: string;
  height?: number;
}

const CHART_COLORS = ["#4DD0E1", "#FF9800", "#1976D2", "#4CAF50", "#F44336"];

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  height = 300,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const formattedTitle = formatChartTitle(title);

  const total = useMemo(
    () =>
      data.reduce(
        (sum, item) => sum + (Number.isFinite(item.value) ? item.value : 0),
        0
      ),
    [data]
  );

  const formatPercentage = (value: number) => {
    if (!Number.isFinite(value)) return "0%";
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
  };

  const chartData = data.map((item, index) => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    return {
      id: index,
      value: item.value,
      label: item.label,
      percentage,
    };
  });

  const colors = data.map(
    (item, index) => item.color || CHART_COLORS[index % CHART_COLORS.length]
  );

  return (
    <Box>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <Box ref={containerRef} sx={{ width: "100%" }}>
        <PieChart
          series={[
            {
              data: chartData,
              innerRadius: 60,
              outerRadius: 100,
              paddingAngle: 2,
              valueFormatter: (item) =>
                formatPercentage((item as { percentage?: number }).percentage ?? 0),
              highlightScope: { fade: "global", highlight: "item" },
            },
          ]}
          colors={colors}
          height={height}
          slotProps={{
            legend: {
              direction: "horizontal",
              position: { vertical: "bottom", horizontal: "center" },
            },
          }}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        />
      </Box>
    </Box>
  );
};
